import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Avatar,
  Dropdown,
  Switch
} from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LogoutOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const roleColors = {
  super_admin: 'red',
  admin: 'orange',
  facilitator: 'blue',
  student: 'green'
};

const roleLabels = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  facilitator: 'Facilitator',
  student: 'Student'
};

export default function UserManagement() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordResetModalVisible, setPasswordResetModalVisible] = useState(false);
  const [resettingUser, setResettingUser] = useState(null);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      message.error('Failed to load users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/super-admin/dashboard'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingUser(record);
    form.setFieldsValue({
      email: record.email,
      first_name: record.first_name,
      last_name: record.last_name,
      role: record.role,
      phone: record.phone || '',
      is_active: record.is_active !== undefined ? record.is_active : true,
    });
    setModalVisible(true);
  };

  const handleDelete = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      message.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      message.error('Failed to delete user');
      console.error('Error deleting user:', error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingUser) {
        // Update existing user
        await api.put(`/users/${editingUser.id}`, values);
        message.success('User updated successfully');
      } else {
        // Create new user
        await api.post('/users', {
          ...values,
          password: values.password || 'TempPass123!' // Temporary password
        });
        message.success('User created successfully');
      }

      setModalVisible(false);
      form.resetFields();
      fetchUsers();
    } catch (error) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error(editingUser ? 'Failed to update user' : 'Failed to create user');
      console.error('Error saving user:', error);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingUser(null);
  };

  const handlePasswordReset = (record) => {
    setResettingUser(record);
    passwordForm.resetFields();
    setPasswordResetModalVisible(true);
  };

  const handlePasswordResetOk = async () => {
    try {
      const values = await passwordForm.validateFields();

      // Validate passwords match
      if (values.new_password !== values.confirm_password) {
        message.error('Passwords do not match');
        return;
      }

      await api.put(`/users/${resettingUser.id}/password`, {
        new_password: values.new_password
      });

      message.success('Password reset successfully');
      setPasswordResetModalVisible(false);
      passwordForm.resetFields();
      setResettingUser(null);
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      message.error('Failed to reset password');
      console.error('Error resetting password:', error);
    }
  };

  const handlePasswordResetCancel = () => {
    setPasswordResetModalVisible(false);
    passwordForm.resetFields();
    setResettingUser(null);
  };

  // Helper function to check if current user can reset password for target user
  const canResetPassword = (targetUser) => {
    const roleHierarchy = {
      super_admin: 4,
      admin: 3,
      facilitator: 2,
      student: 1
    };

    // Can only reset password for users with lower role
    return roleHierarchy[user?.role] > roleHierarchy[targetUser.role];
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <Space>
          <Avatar style={{ backgroundColor: '#faad14' }} icon={<UserOutlined />} />
          <div>
            <div><Text strong>{record.first_name} {record.last_name}</Text></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text></div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={roleColors[role]}>
          {roleLabels[role]}
        </Tag>
      ),
      filters: [
        { text: 'Super Admin', value: 'super_admin' },
        { text: 'Admin', value: 'admin' },
        { text: 'Facilitator', value: 'facilitator' },
        { text: 'Student', value: 'student' },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => phone || <Text type="secondary">N/A</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          {canResetPassword(record) && (
            <Button
              type="link"
              icon={<KeyOutlined />}
              onClick={() => handlePasswordReset(record)}
            >
              Reset Password
            </Button>
          )}
          <Popconfirm
            title="Delete user"
            description="Are you sure you want to delete this user?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#faad14', cursor: 'pointer' }} onClick={() => navigate('/super-admin/dashboard')}>
            USSEDU Platform
          </Title>
          <Text type="secondary">User Management</Text>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2}>User Management</Title>
            <Text type="secondary">Manage system users and their roles</Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleCreate}
          >
            Add User
          </Button>
        </div>

        <Card>
          <Table
            columns={columns}
            dataSource={users}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} users`,
            }}
          />
        </Card>
      </Content>

      <Modal
        title={editingUser ? 'Edit User' : 'Create New User'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        okText={editingUser ? 'Update' : 'Create'}
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ color: '#faad14' }} />}
              placeholder="user@example.com"
              disabled={!!editingUser}
            />
          </Form.Item>

          <Space style={{ width: '100%' }} size="large">
            <Form.Item
              name="first_name"
              label="First Name"
              rules={[{ required: true, message: 'Please enter first name' }]}
              style={{ flex: 1, marginBottom: 0 }}
            >
              <Input placeholder="John" />
            </Form.Item>

            <Form.Item
              name="last_name"
              label="Last Name"
              rules={[{ required: true, message: 'Please enter last name' }]}
              style={{ flex: 1, marginBottom: 0 }}
            >
              <Input placeholder="Doe" />
            </Form.Item>
          </Space>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select placeholder="Select role">
              <Select.Option value="student">Student</Select.Option>
              <Select.Option value="facilitator">Facilitator</Select.Option>
              <Select.Option value="admin">Admin</Select.Option>
              <Select.Option value="super_admin">Super Admin</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
          >
            <Input
              prefix={<PhoneOutlined style={{ color: '#faad14' }} />}
              placeholder="+1234567890"
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Inactive"
              defaultChecked
            />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter password' },
                { min: 8, message: 'Password must be at least 8 characters' }
              ]}
            >
              <Input.Password placeholder="Enter password" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title="Reset Password"
        open={passwordResetModalVisible}
        onOk={handlePasswordResetOk}
        onCancel={handlePasswordResetCancel}
        width={500}
        okText="Reset Password"
      >
        <div style={{ marginBottom: 16 }}>
          <Text>Reset password for: </Text>
          <Text strong>
            {resettingUser?.first_name} {resettingUser?.last_name} ({resettingUser?.email})
          </Text>
        </div>

        <Form
          form={passwordForm}
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            name="new_password"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 8, message: 'Password must be at least 8 characters' }
            ]}
          >
            <Input.Password
              placeholder="Enter new password"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label="Confirm Password"
            rules={[
              { required: true, message: 'Please confirm password' },
              { min: 8, message: 'Password must be at least 8 characters' }
            ]}
          >
            <Input.Password
              placeholder="Confirm new password"
              autoComplete="new-password"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
